// modules
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

// constants
import { DateTimeFormats, ReportGeneration as Constants, Routes } from "constants/global";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// redux
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { getConfigData, getConfigSelector, getStoreList, getStoreListSelector } from "redux/userList/userSlice";

// types
import { ConfigDataItem, OutletItem, RetroClaimReportPayload } from "services/api/types";

// services
import { getAdminReimbRejectionReport, getAdminReimbVoucherReport, getTenantVouchRedemptionReport, getRetroClaimReport } from "services/api/reimbursement";

// utils
import { downloadFile } from "utils/downloadFile";
import { isFromCalenderError, isToCalenderError } from "utils/CalenderError";
import moment from "moment";
import { getOutlets } from "utils/Generic/generic";

type ReportType = {
	type: string | null;
	selected: boolean;
};

type VoucherType = {
	type: string | null;
	selected: boolean;
};

type RejectReportPayload = {
	from_year_month?: string;
	to_year_month?: string;
	outlet_code?: string;
	tenant_code?: string;
	voucher_group_code?: string;
	terminal?: string;
};

type TenantVouRedemptionReportPayload = {
	from_date: string;
	to_date: string;
	outlets: string[];
	voucher_group_code: string;
};

export type DropdownItem = { id: string; value: string; label?: string };

export function useReportGenerationAdmin() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const configDetails = useAppSelector(getConfigSelector);
	const storeDetetails = useAppSelector(getStoreListSelector);

	const { data: configDetailsData } = configDetails || {};
	const { data: storeDetetailsData } = storeDetetails || {};

	const [reportType, setReportType] = useState<ReportType>({ type: null, selected: false });
	const [voucherType, setVoucherType] = useState<VoucherType>({ type: null, selected: false });
	const [terminal, setTerminalType] = useState<string | null>(null);
	const [fromMonth, setFromMonth] = useState<string | null>(null);
	const [fromYear, setFromYear] = useState<number | null>(null);
	const [toMonth, setToMonth] = useState<string | null>(null);
	const [toYear, setToYear] = useState<number | null>(null);
	const [tenantName, setTenantName] = useState<{ name: string; input: boolean }>({ name: "", input: false });
	const [outletName, setOutletName] = useState<{ name: string; input: boolean }>({ name: "", input: false });

	const [tenantSearchSelected, setTenantSearchSelected] = useState<boolean>(false);
	const [tenantSearchInput, setTenantSearchInput] = useState<string | null>(null);

	const [outletSearchSelected, setOutletSearchSelected] = useState<boolean>(false);
	const [outletSearchInput, setOutletSearchInput] = useState<string | null>(null);

	const [loading, setLoading] = useState<boolean>(false);
	const [voucherRedemptionStartDate, setVoucherRedemptionStartDate] = useState<string>("");
	const [voucherRedemptionEndDate, setVoucherRedemptionEndDate] = useState<string>("");
	const [dateRangeError, setDateRangeError] = useState<boolean>(false);

	useEffect(() => {
		dispatch(getConfigData({ config_mst_code: Constants.CONFIG_MASTER_CODE_VOUCHER_TYPES }));
		dispatch(getStoreList({}));
	}, []);

	const voucherTypesList = useMemo(() => {
		return (configDetailsData || [])?.map((_config: ConfigDataItem) => ({
			id: _config.config_dtl_code,
			value: _config.config_dtl_code,
			label: _config.config_dtl_desc
		}));
	}, [configDetailsData]);

	const { terminalsList, outletsList, tenantsList } = useMemo(() => {
		const terminals: DropdownItem[] = [];
		const tenants: DropdownItem[] = [];
		const outlets: OutletItem[] = [];

		(storeDetetailsData || [])?.forEach((store: OutletItem) => {
			if (store?.terminal && !terminals?.find(({ id }) => id === store?.terminal)) {
				terminals.push({ id: store?.terminal, value: store?.terminal });
			}
			if (store?.tenant_code && !tenants?.find(({ id }) => id === store?.tenant_code)) {
				tenants.push({ id: store?.tenant_code, value: store?.tenant_code, label: store?.tenant_name });
			}
			if (terminal && store?.outlet_code && !outlets?.find(({ outlet_code }) => outlet_code === store?.outlet_code) && store?.terminal === terminal) {
				outlets.push(store);
			}
			if (!terminal && store?.outlet_code && !outlets?.find(({ outlet_code }) => outlet_code === store?.outlet_code)) {
				outlets.push(store);
			}
		});

		return {
			terminalsList: terminals,
			outletsList: outlets,
			tenantsList: tenants
		};
	}, [terminal, storeDetetailsData]);

	const handleReportType = useCallback(
		(value: string) => {
			setReportType({ ...reportType, type: value, selected: true });
		},
		[reportType, setReportType]
	);

	const handleVoucherType = useCallback(
		(value: string) => {
			setVoucherType({ ...voucherType, type: value, selected: true });
		},
		[voucherType, setVoucherType]
	);

	const handleTenantName = useCallback(
		(value: string) => {
			if (value) {
				setTenantName({ ...tenantName, name: value, input: true });
				setOutletName({ ...outletName, name: "", input: false });
			} else {
				setOutletName({ ...outletName, name: "", input: false });
				setTenantName({ ...tenantName, name: "", input: false });
				setFromMonth(null);
				setFromYear(null);
			}
		},
		[tenantName, outletName, fromMonth, fromYear]
	);

	const onTenantSearch = useCallback(
		(value: string) => {
			if (value) {
				setTenantSearchInput(value);
				setTenantSearchSelected(true);
			} else {
				setTenantSearchInput(null);
				setTenantSearchSelected(false);
			}
		},
		[tenantSearchInput, tenantSearchSelected]
	);

	const handleOutletName = useCallback(
		(value: string) => {
			if (value) {
				setOutletName({ ...outletName, name: value, input: true });
				setTenantName({ ...tenantName, name: "", input: true });
			} else {
				setOutletName({ ...outletName, name: "", input: false });
				setTenantName({ ...tenantName, name: "", input: false });
				setFromMonth(null);
				setFromYear(null);
			}
		},

		[tenantName, outletName, fromMonth, fromYear]
	);

	const onOutletSearch = useCallback(
		(value: string) => {
			if (value) {
				setOutletSearchInput(value);
				setOutletSearchSelected(true);
			} else {
				setOutletSearchInput(null);
				setOutletSearchSelected(false);
			}
		},
		[outletSearchInput, outletSearchSelected]
	);

	const handleFromMonth = useCallback(
		(value: string) => {
			setFromMonth(value);
		},

		[fromMonth, setFromMonth]
	);

	const handleTerminalType = useCallback(
		(value: string) => {
			setTerminalType(value);
			setOutletName({ ...tenantName, name: "", input: false });
			setTenantName({ ...tenantName, name: "", input: false });
		},
		[terminal, setTerminalType]
	);

	const handleFromYear = useCallback(
		(value: number) => {
			setFromYear(value);
			setToYear(null);
		},
		[fromYear, setFromYear]
	);

	const handleToMonth = useCallback(
		(value: string) => {
			setToMonth(value);
		},
		[toMonth, setToMonth]
	);

	const handleToYear = useCallback(
		(value: number) => {
			setToYear(value);
		},
		[toYear, setToYear]
	);

	const toError = useMemo(() => {
		return isToCalenderError(fromYear, toMonth, fromMonth);
	}, [fromYear, toMonth, fromMonth, isToCalenderError]);

	const fromError = useMemo(() => {
		return isFromCalenderError(fromYear, fromMonth);
	}, [fromMonth, fromYear, isFromCalenderError]);

	const isDownloadButtonEnabled = useCallback(() => {
		if (reportType?.type === Constants.ADMIN_REPORT_TYPE[2].value || reportType?.type === Constants.ADMIN_REPORT_TYPE[3].value) {
			return !!(voucherRedemptionStartDate && !dateRangeError && reportType.selected && !fromError && !toError);
		}
		return !!(fromMonth && fromYear && reportType.selected && !fromError && !toError);
	}, [fromMonth, fromYear, reportType, toMonth, toYear, fromError, toError, voucherRedemptionStartDate, tenantName, outletName, dateRangeError]);

	const onSuccess = useCallback(() => {
		navigate(Routes.REPORTS_ADMIN);
	}, [navigate]);

	const generateRejectedDeclarationPayload = useCallback(() => {
		const tempPayload: RejectReportPayload = {
			from_year_month: `${fromYear}${fromMonth}`,
			to_year_month: `${toYear || fromYear}${toMonth || fromMonth}`,
			outlet_code: `${outletName?.name || ""}`,
			tenant_code: `${tenantName?.name || ""}`,
			voucher_group_code: `${voucherType?.type || ""}`,
			terminal: `${terminal || ""}`
		};

		const payload: RejectReportPayload = {};

		Object.keys(tempPayload).forEach((key) => {
			if (tempPayload[key as keyof RejectReportPayload]) {
				payload[key as keyof RejectReportPayload] = tempPayload[key as keyof RejectReportPayload];
			}
		});

		return payload;
	}, [fromMonth, fromYear, toYear, toMonth, outletName, tenantName, voucherType]);

	const resetAll = () => {
		setReportType({ ...reportType, type: null, selected: false });
		setVoucherType({ ...voucherType, type: null, selected: false });
		setTerminalType(null);
		setFromMonth(null);
		setFromYear(null);
		setToMonth(null);
		setToYear(null);
		setTenantName({ ...tenantName, name: "", input: false });
		setOutletName({ ...outletName, name: "", input: false });
		setDateRangeError(false);
		setVoucherRedemptionStartDate("");
		setVoucherRedemptionEndDate("");
	};

	const onFailure = useCallback(() => {
		setLoading(false);
		CountDownModal({
			timer: 3,
			errorIcon: true,
			title: Constants.NO_DATA_FOUND,
			onTimeComplete: onSuccess
		});
	}, []);

	const generateRedemptionReportPayload = useCallback(() => {
		const outlets = getOutlets(outletsList, tenantName, outletName);

		const payload: TenantVouRedemptionReportPayload = {
			from_date: `${moment(voucherRedemptionStartDate).format(DateTimeFormats.USE_YYYYMMDD)}`,
			to_date: `${voucherRedemptionStartDate && voucherRedemptionEndDate ? moment(voucherRedemptionEndDate).format(DateTimeFormats.USE_YYYYMMDD) : ""}`,
			outlets,
			voucher_group_code: voucherType?.type ? voucherType?.type : ""
		};
		return payload;
	}, [getOutlets, reportType, voucherRedemptionStartDate, voucherRedemptionEndDate, outletName, tenantName, outletsList, voucherType]);

	const generateRetroClaimReportPayload = useCallback(() => {
		const outlets = getOutlets(outletsList, tenantName, outletName);

		const payload: RetroClaimReportPayload = {
			from_date: `${moment(voucherRedemptionStartDate).format(DateTimeFormats.USE_YYYYMMDD)}`,
			to_date: `${voucherRedemptionStartDate && voucherRedemptionEndDate ? moment(voucherRedemptionEndDate).format(DateTimeFormats.USE_YYYYMMDD) : ""}`,
			outlets
		};
		return payload;
	}, [getOutlets, reportType, voucherRedemptionStartDate, voucherRedemptionEndDate, outletName, tenantName, outletsList, voucherType]);

	const getReport: any = useCallback(async () => {
		switch (reportType?.type) {
			case Constants.ADMIN_REPORT_TYPE[0].value:
				// Rejected/Revised Declaration Report
				try {
					const rejectionReportPayload = generateRejectedDeclarationPayload();
					const response = await getAdminReimbRejectionReport(rejectionReportPayload);

					return response?.data;
				} catch (error) {
					onFailure();
				}
				break;
			case Constants.ADMIN_REPORT_TYPE[1].value:
				// Voucher Reimbursement Report
				try {
					const reimbVoucherReportPayload = generateRejectedDeclarationPayload();
					const response = await getAdminReimbVoucherReport(reimbVoucherReportPayload);

					return response?.data;
				} catch (error) {
					onFailure();
				}
				break;
			case Constants.ADMIN_REPORT_TYPE[2].value:
				// Voucher Redemption Report
				try {
					const redemptionReportPayload = generateRedemptionReportPayload();
					const response = await getTenantVouchRedemptionReport(redemptionReportPayload);

					return response?.data;
				} catch (error) {
					onFailure();
				}
				break;

			case Constants.ADMIN_REPORT_TYPE[3].value:
				// Retrospective Claims Reports
				try {
					const redemptionReportPayload = generateRetroClaimReportPayload();
					const response = await getRetroClaimReport(redemptionReportPayload);

					return response?.data;
				} catch (error) {
					onFailure();
				}
				break;
			default:
				break;
		}
	}, [reportType, onFailure, generateRetroClaimReportPayload, generateRedemptionReportPayload, generateRejectedDeclarationPayload]);

	const proceedToGenerateReport = useCallback(async () => {
		if (isDownloadButtonEnabled()) {
			try {
				setLoading(true);
				const reportsData = await getReport();

				const data = reportsData?.data;
				const errors = reportsData?.errors;

				if (data?.file_url && !errors?.length) {
					setLoading(false);
					try {
						await downloadFile(data?.file_url);

						CountDownModal({
							timer: 3,
							title: Constants.REPORT_GENERATED,
							onTimeComplete: onSuccess
						});
						resetAll();
					} catch (error) {
						setLoading(false);
						onFailure();
					}
				} else if (errors?.length) {
					onFailure();
				}
			} catch (error) {
				onFailure();
			} finally {
				setLoading(false);
			}
		}
	}, [reportType, getReport, onSuccess, generateRejectedDeclarationPayload, isDownloadButtonEnabled]);

	const handleVoucherRedemptionDate = useCallback(
		(_type: string, _date: string) => {
			if (_type === Constants.DATE_TYPE.END_DATE) {
				if (dateRangeError) {
					setDateRangeError(false);
				}
				const startDate = moment();
				const endDate = moment(_date);

				if (startDate < endDate) {
					setDateRangeError(true);
				}
				setVoucherRedemptionEndDate(_date);
			}

			if (_type === Constants.DATE_TYPE.START_DATE) {
				setVoucherRedemptionStartDate(_date);
			}
		},
		[voucherRedemptionEndDate, voucherRedemptionStartDate]
	);

	return {
		terminal,
		reportType,
		voucherType,
		fromMonth,
		fromYear,
		toMonth,
		toYear,
		fromError,
		toError,
		loading,
		outletName,
		tenantName,
		voucherTypesList,
		terminalsList,
		outletsList,
		tenantsList,
		tenantSearchInput,
		outletSearchInput,
		outletSearchSelected,
		tenantSearchSelected,
		voucherRedemptionStartDate,
		voucherRedemptionEndDate,
		dateRangeError,
		handleTenantName,
		onTenantSearch,
		handleOutletName,
		onOutletSearch,
		handleTerminalType,
		handleFromMonth,
		handleFromYear,
		handleToMonth,
		handleToYear,
		onSuccess,
		resetAll,
		handleReportType,
		handleVoucherType,
		isDownloadButtonEnabled,
		proceedToGenerateReport,
		handleVoucherRedemptionDate
	};
}
